import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  FormControl,
  Menu,
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useAppState } from 'store/Provider';
import { selectFilterAction, selectFilterRemoveAction } from 'store/actions/filterActions';
import { changePageAction } from 'store/actions/dashboardActions';

interface OptionType {
  id: string | number;
  name: string;
}

interface CollapsibleSelectFilterProps {
  name: string;
  apiKeyname: string;
  label: string;
  disabled: boolean;
  mockOptions: OptionType[];
  value: OptionType[];
  onChange: (selectedValues: OptionType[]) => void;
  clearPerticularFilters: (apiKeyname: string) => void;
}

const CollapsibleSelectFilter: React.FC<CollapsibleSelectFilterProps> = ({
  name,
  apiKeyname,
  label,
  disabled,
  mockOptions,
  value,
  onChange,
  clearPerticularFilters,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const { dispatch } = useAppState();
  const [checkedOptions, setCheckedOptions] = useState<Record<string | number, boolean>>({});
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  // Parse initial values
  useEffect(() => {
    const initial: Record<string | number, boolean> = {};
    let selectedIds: Array<string | number> = [];
    if (value === 'ALL') {
      // Select all checkboxes if value is 'ALL'
      mockOptions?.forEach((opt) => {
        initial[opt.id] = true;
      });
      setCheckedOptions(initial);
      setIsSelectAllChecked(true);
    } else {
      if (Array.isArray(value)) {
        selectedIds = value.map((v) => v.id);
      }
      mockOptions?.forEach((opt) => {
        initial[opt.id] = selectedIds.includes(opt.id);
      });
      setCheckedOptions(initial);
      setIsSelectAllChecked(mockOptions?.length > 1 && selectedIds.length === mockOptions?.length);
    }
  }, [mockOptions, value]);

  const updateSelectAllState = (updated: Record<string | number, boolean>) => {
    const all = mockOptions?.length > 0 && mockOptions?.every((opt) => updated[opt.id]);
    setIsSelectAllChecked(all);
  };

  const handleCheckboxChange = (key: string | number, checked: boolean) => {
    setCheckedOptions((prev) => {
      const updated = { ...prev, [key]: checked };
      updateSelectAllState(updated);
      return updated;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    const updated: Record<string | number, boolean> = {};
    mockOptions?.forEach((opt) => {
      updated[opt.id] = checked;
    });
    setCheckedOptions(updated);
    setIsSelectAllChecked(checked);
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setSearch('');
    setOpen(false);
  };

  const handleApply = () => {
    const selected = mockOptions?.filter((opt) => checkedOptions[opt.id]);

    if (isSelectAllChecked && selected.length > 1) {
      dispatch(selectFilterAction({ [apiKeyname]: ['ALL'] }));
      onChange('ALL');
    } else {
      if (selected.length === 1) {
        dispatch(selectFilterAction({ [apiKeyname]: [selected[0].id.toString()] }));
      } else {
        dispatch(selectFilterAction({ [apiKeyname]: selected.map((s) => s.id.toString()) }));
      }
      onChange(selected);
    }
    handleClose();
  };

  const perticularFieldClear = () => {
    dispatch(selectFilterAction({ [apiKeyname]: ['ALL'] }));
    onChange('ALL');
  };

  const handleClear = (field: string, apiKeyname: string) => {
    onChange([]);
    setCheckedOptions({});
    clearPerticularFilters(apiKeyname);
    dispatch(selectFilterRemoveAction({ [field]: 'ALL' }));
    dispatch(changePageAction(0));
    setIsSelectAllChecked(true);
    perticularFieldClear();
    handleClose();
  };

  // Fix displayValue for array or string, and show 'ALL' if selected
  let displayValue = '';
  if (value === 'ALL') {
    displayValue = 'ALL';
  } else if (Array.isArray(value)) {
    displayValue = value.map((v) => v.name).join(', ');
  }

  // Search state
  const [search, setSearch] = useState('');
  const filteredOptions = mockOptions?.filter((opt) =>
    String(opt?.name ?? '')
      .toLowerCase()
      .includes(search?.toLowerCase() ?? '')
  );

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <Box sx={{ mb: 2 }}>
      <FormControl fullWidth>
        {/* Label outside the TextField */}
        <Typography
          component="label"
          htmlFor={`collapsible-select-filter-${name}`}
          sx={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'primary.main',
            mb: 0.5,
            display: 'block',
          }}
        >
          {label}
        </Typography>

        <TextField
          id={`collapsible-select-filter-${name}`}
          value={displayValue}
          onClick={handleOpen}
          readOnly
          fullWidth
          size="small"
          placeholder="Select..."
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {displayValue && displayValue !== 'ALL' && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear(name, apiKeyname);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                <ArrowDropDownIcon sx={{ cursor: 'pointer', color: 'action.active' }} />
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: anchorEl ? anchorEl.clientWidth : 320,
            maxHeight: 450,
            mt: 0.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Search Input - Fixed at top */}
        <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f5f5f5',
              },
            }}
          />
        </Box>

        <MenuItem onMouseDown={stop} sx={{ py: 1, px: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Checkbox
            size="small"
            checked={isSelectAllChecked}
            onChange={(e) => handleSelectAll(e.target.checked)}
            sx={{ p: 0.5, mr: 1 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
            ALL
          </Typography>
        </MenuItem>
        {/* Scrollable Content Area */}
        <Box sx={{ overflowY: 'auto', flexGrow: 1, maxHeight: 250 }}>
          {/* ALL Checkbox */}

          {/* Options List */}

          {filteredOptions?.map((opt) => (
            <MenuItem
              key={opt.id}
              onMouseDown={stop}
              sx={{
                py: 1.2,
                px: 1.5,
                minHeight: 50,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
              disabled={disabled}
            >
              <Checkbox
                size="small"
                checked={checkedOptions[opt.id] || false}
                onChange={(e) => handleCheckboxChange(opt.id, e.target.checked)}
                disabled={disabled}
                sx={{ p: 0.5, mr: 1 }}
              />
              <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                {opt.name}
              </Typography>
            </MenuItem>
          ))}
        </Box>

        {/* Action Buttons - Fixed at bottom */}
        <Box
          onMouseDown={stop}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 1,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
            flexShrink: 0,
          }}
        >
          <Button size="small" onClick={handleClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleApply} sx={{ textTransform: 'none' }}>
            Apply
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default CollapsibleSelectFilter;
